import { MsalProvider } from '@azure/msal-react'
import { DateFnsLocaleProvider } from '@if/core/date'
import i18n, { initializeI18N } from '@if/core/i18n'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRoot } from 'react-dom/client'
import { I18nextProvider } from 'react-i18next'
import ErrorBoundary from './components/Common/Error/ErrorBoundary'
import Router from './containers/Router'
import AuthInterceptorProvider from './providers/AuthInterceptorProvider'
import ConflictHandler from './providers/ConflictHandler'
import GlobalLoaderProvider from './providers/GlobalLoaderProvider'
import LocaleProvider from './providers/LocaleProvider'
import NotificationProvider from './providers/NotificationProvider'
import OnboardingProvider from './providers/OnboardingProvider'
import ThemeProvider from './providers/ThemeProvider'
import UserProvider from './providers/UserProvider'
import initializeAppInsights from './services/AppInsights'
import initializeMsal from './services/Authentification'
import { reportErrorToConsole, setupTracking } from './services/error-logger'

const ai = initializeAppInsights()
setupTracking(ai)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      retry: false,
    },
  },
})
const container = document.getElementById('root')
const root = createRoot(container!)
const msalInstance = initializeMsal()

initializeI18N()
  .then(() => {
    root.render(
      <I18nextProvider i18n={i18n}>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <DateFnsLocaleProvider>
                <LocaleProvider>
                  <NotificationProvider>
                    <AuthInterceptorProvider>
                    <ConflictHandler>
                      <UserProvider>
                        <OnboardingProvider>
                          <ErrorBoundary>
                            <GlobalLoaderProvider>
                              <Router />
                              <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
                            </GlobalLoaderProvider>
                          </ErrorBoundary>
                        </OnboardingProvider>
                      </UserProvider>
                    </ConflictHandler>
                    </AuthInterceptorProvider>
                  </NotificationProvider>
                </LocaleProvider>
              </DateFnsLocaleProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </MsalProvider>
      </I18nextProvider>,
    )
  })
  .catch(reportErrorToConsole)
